/**
 * Hook useAssistantChat - Gère la logique du chat avec l'agent RASK
 * Clean Architecture - Presentation Layer
 * 
 * Ce hook encapsule toute la logique métier et expose une API simple
 * pour le composant AssistantChat
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../../firebase';
import { useAuth } from '../../../core/providers/AuthProvider';
import { usePreferences } from '../../../shared/hooks/usePreferences';
import buildAgentContext from '../../../shared/agent/agentContextBuilder';
import { Conversation } from '../../../domain/agent/entities/Conversation';
import { MessageFactory } from '../../../domain/agent/entities/Message';
import SendMessageToAgent, { ConversationError } from '../../../use-cases/agent/SendMessageToAgent';
import { repositories } from '../../../infrastructure/config/repositories';

/**
 * Constantes
 */
const WELCOME_MESSAGE_ID = 'welcome';
const GPS_TTL_MS = 10 * 60 * 1000;

/**
 * Récupère la localisation de l'utilisateur (Presentation Layer)
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
const getGpsLocation = () => {
  return new Promise((resolve) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          // error.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
          resolve(null);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    } else {
      resolve(null);
    }
  });
};

/**
 * Hook pour gérer le chat avec l'assistant RASK
 * @returns {Object} API du chat
 */
export const useAssistantChat = () => {
  const { t } = useTranslation('posts');
  const { user } = useAuth();
  const { preferences } = usePreferences();
  
  // State
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs
  const conversationRef = useRef(null);
  const sendMessageUseCaseRef = useRef(null);
  const lastGpsRef = useRef({ location: null, timestamp: 0 });

  // Initialiser la conversation et le use-case
  useEffect(() => {
    // Créer une nouvelle conversation
    conversationRef.current = new Conversation();
    
    // Créer le use-case avec le repository
    sendMessageUseCaseRef.current = new SendMessageToAgent(repositories.agent);
    
    // Ajouter le message de bienvenue
    const welcomeMessage = MessageFactory.createSystemMessage(
      t('assistant.welcomeMessage'),
      WELCOME_MESSAGE_ID
    );
    // Transformer en format assistant pour l'affichage
    welcomeMessage.type = 'assistant';
    welcomeMessage.role = 'assistant';
    
    setMessages([welcomeMessage]);
    // Note: On n'inclut pas `t` dans les deps pour éviter un reset lors du changement de langue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Met à jour uniquement le message de bienvenue lors du changement de langue
  useEffect(() => {
    setMessages(prevMessages => {
      if (
        prevMessages.length > 0 &&
        prevMessages[0].id === WELCOME_MESSAGE_ID &&
        prevMessages[0].role === 'assistant'
      ) {
        // Créer un nouveau message de bienvenue traduit
        const updatedWelcome = {
          ...prevMessages[0],
          content: t('assistant.welcomeMessage'),
        };
        return [updatedWelcome, ...prevMessages.slice(1)];
      }
      return prevMessages;
    });
  }, [t]);

  /**
   * Envoie un message à l'agent
   * @param {string} text - Texte du message (peut être vide si uiAction est fourni)
   * @param {Object} [options] - Options supplémentaires pour l'envoi du message
   * @param {string} [options.uiAction] - Identifiant d'une action d'UI (e.g., 'progress_to_l2', 'progress_to_l3', 'confirm_create_activity')
   * @param {Object} [options.pendingActivity] - Données de l'activité en attente de confirmation (utilisé avec uiAction 'confirm_create_activity')
   */
  const sendMessage = useCallback(async (text, options = {}) => {
    if ((!text?.trim() && !options.uiAction) || isLoading) return;

    setIsLoading(true);
    setError(null);

    // Créer et afficher immédiatement le message utilisateur (sauf si uiAction seul)
    const messageText = text ? text.trim() : '';
    if (messageText) {
      const userMessage = MessageFactory.createUserMessage(messageText);
      userMessage.role = 'user'; // Pour compatibilité avec le composant existant
      
      setMessages(prev => [...prev, userMessage]);
    }

    try {
      // Récupérer le token Firebase
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        throw new ConversationError('AUTH_REQUIRED', null, { code: 'SESSION_EXPIRED' });
      }

      // Priorité: currentPosition (préférence manuelle) > GPS temps réel > profil
      const currentPosition = preferences?.currentPosition;
      let gpsLocation = null;
      
      if (currentPosition?.latitude && currentPosition?.longitude) {
        // Utiliser la position enregistrée manuellement dans les préférences
        gpsLocation = {
          latitude: currentPosition.latitude,
          longitude: currentPosition.longitude
        };
      } else {
        // Sinon, récupérer la position GPS en temps réel
        const now = Date.now();
        gpsLocation = lastGpsRef.current.location;
        const isGpsFresh = now - lastGpsRef.current.timestamp < GPS_TTL_MS;
        // Ne pas cacher un refus GPS (null) - toujours réessayer si null
        if (!isGpsFresh || !gpsLocation) {
          gpsLocation = await getGpsLocation();
          // Ne cacher que si on a obtenu une position valide
          if (gpsLocation) {
            lastGpsRef.current = { location: gpsLocation, timestamp: now };
          }
        }
      }

      const baseContext = buildAgentContext({
        gpsLocation,
        profileLocation: user?.location,
        preferences,
        interests: user?.interests,
        platform: 'web',
        outputFormat: 'ui_result_v1',
        uiAction: options.uiAction, // RM-149: Progression externe L1→L2→L3
      });
      // Merge pendingActivity if provided (for confirm_create_activity uiAction)
      const context = options.pendingActivity
        ? { ...baseContext, pendingActivity: options.pendingActivity }
        : baseContext;

      // Exécuter le use-case
      const result = await sendMessageUseCaseRef.current.execute({
        message: messageText || '',
        conversation: conversationRef.current,
        authToken: token,
        context,
      });

      // Transformer le message assistant pour compatibilité
      const assistantMessage = {
        ...result.assistantMessage,
        role: 'assistant',
        actionCards: result.assistantMessage.cards,
        uiResult: result.assistantMessage.uiResult,
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      // Déterminer le message d'erreur basé sur errorCode (standardisé)
      const errorCode = err.errorCode || err.cause?.errorCode || err.context?.code;
      const statusCode = err.statusCode || err.cause?.statusCode || err.context?.statusCode;
      
      let errorMessage;
      if (errorCode === 'SESSION_EXPIRED' || errorCode === 'AUTH_REQUIRED') {
        errorMessage = t('assistant.errors.sessionExpired');
      } else if (errorCode === 'TIMEOUT' || statusCode === 504) {
        errorMessage = t('assistant.errors.timeout');
      } else if (errorCode === 'SERVER_ERROR' || statusCode >= 500) {
        errorMessage = t('assistant.errors.serverError');
      } else if (errorCode === 'NETWORK_ERROR' || !navigator.onLine) {
        errorMessage = t('assistant.errors.networkError');
      } else {
        errorMessage = t('assistant.errors.unknown');
      }

      setError(errorMessage);

      // Créer et afficher le message d'erreur
      const errorMsg = MessageFactory.createErrorMessage(errorMessage);
      errorMsg.role = 'assistant';
      
      setMessages(prev => [...prev, errorMsg]);

    } finally {
      setIsLoading(false);
    }
  }, [isLoading, t, user, preferences]);

  /**
   * Efface la conversation et recommence
   */
  const clearConversation = useCallback(() => {
    // Créer une nouvelle conversation avec un nouvel ID pour repartir de zéro
    conversationRef.current = new Conversation();
    
    // Réinitialiser avec le message de bienvenue
    const welcomeMessage = MessageFactory.createSystemMessage(
      t('assistant.welcomeMessage'),
      'welcome'
    );
    welcomeMessage.type = 'assistant';
    welcomeMessage.role = 'assistant';
    
    setMessages([welcomeMessage]);
    setError(null);
  }, [t]);

  /**
   * Réessaie le dernier message en cas d'erreur
   */
  const retryLastMessage = useCallback(async () => {
    const lastUserMessage = conversationRef.current?.getLastUserMessage();
    if (lastUserMessage && error) {
      setError(null);
      // Retirer le dernier message d'erreur
      setMessages(prev => prev.filter(m => !m.error));
      await sendMessage(lastUserMessage.content);
    }
  }, [error, sendMessage]);

  /**
   * Récupère les statistiques de la conversation
   */
  const getStats = useCallback(() => {
    return conversationRef.current?.getStats() || {
      messageCount: messages.length,
      userMessages: messages.filter(m => m.role === 'user').length,
      assistantMessages: messages.filter(m => m.role === 'assistant').length,
      errorMessages: messages.filter(m => m.error).length,
    };
  }, [messages]);

  return {
    // State
    messages,
    isLoading,
    error,
    
    // Actions
    sendMessage,
    clearConversation,
    retryLastMessage,
    
    // Utils
    getStats,
    conversation: conversationRef.current,
  };
};

export default useAssistantChat;
